import {useEffect, useMemo, useRef, useState} from 'react';
import {Keyboard, Platform} from 'react-native';

import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import type {
  HealthcareProviderErrors,
  Option,
  UseHealthcareProviderParams,
} from './HealthcareProviderScreen.types';

const normalizeText = (value: string): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const findOptionByValueOrLabel = (
  input: string,
  options: Option[],
): Option | undefined => {
  if (!input) {
    return undefined;
  }

  const normalizedInput = normalizeText(input);

  const valueMatch = options.find(
    option => normalizeText(String(option.value)) === normalizedInput,
  );

  if (valueMatch) {
    return valueMatch;
  }

  return options.find(
    option => normalizeText(String(option.label)) === normalizedInput,
  );
};

const resolveRegisteredField = (
  initialRegisteredField: string,
  initialOtherRegisteredField: string,
  options: Option[],
): {
  registeredField: string;
  otherRegisteredField: string;
} => {
  const rawField = String(initialRegisteredField ?? '').trim();
  const rawOther = String(initialOtherRegisteredField ?? '').trim();

  if (!rawField && !rawOther) {
    return {
      registeredField: '',
      otherRegisteredField: '',
    };
  }

  if (normalizeText(rawField) === 'other') {
    const otherMatch = findOptionByValueOrLabel(rawOther, options);

    if (otherMatch && normalizeText(String(otherMatch.value)) !== 'other') {
      return {
        registeredField: String(otherMatch.value),
        otherRegisteredField: '',
      };
    }

    return {
      registeredField: 'other',
      otherRegisteredField: rawOther,
    };
  }

  const directMatch = findOptionByValueOrLabel(rawField, options);

  if (directMatch) {
    const matchedValue = String(directMatch.value);

    if (normalizeText(matchedValue) === 'other') {
      const otherMatch = findOptionByValueOrLabel(rawOther, options);

      if (otherMatch && normalizeText(String(otherMatch.value)) !== 'other') {
        return {
          registeredField: String(otherMatch.value),
          otherRegisteredField: '',
        };
      }

      return {
        registeredField: 'other',
        otherRegisteredField: rawOther,
      };
    }

    return {
      registeredField: matchedValue,
      otherRegisteredField: '',
    };
  }

  return {
    registeredField: 'other',
    otherRegisteredField: rawField,
  };
};

const resolveRegisteredState = (
  initialRegisteredState: string,
  options: Option[],
): string => {
  const rawState = String(initialRegisteredState ?? '').trim();

  if (!rawState) {
    return '';
  }

  const match = findOptionByValueOrLabel(rawState, options);
  return match ? String(match.value) : '';
};

export const useHealthcareProvider = ({
  initialIsHealthcareProvider = null,
  initialRegisteredField = '',
  initialRegisteredState = '',
  initialLicenseNumber = '',
  initialOtherRegisteredField = '',
  onSave,
  registeredFieldOptions,
  stateOptions,
  scrollRef,
}: UseHealthcareProviderParams) => {
  const previousInitialRef = useRef<string>('');

  const normalizedInitial = useMemo(() => {
    const normalizedField = resolveRegisteredField(
      initialRegisteredField,
      initialOtherRegisteredField,
      registeredFieldOptions,
    );

    return {
      isHealthcareProvider: initialIsHealthcareProvider,
      registeredField: normalizedField.registeredField,
      registeredState: resolveRegisteredState(
        initialRegisteredState,
        stateOptions,
      ),
      licenseNumber: String(initialLicenseNumber ?? '').trim(),
      otherRegisteredField: normalizedField.otherRegisteredField,
    };
  }, [
    initialIsHealthcareProvider,
    initialRegisteredField,
    initialRegisteredState,
    initialLicenseNumber,
    initialOtherRegisteredField,
    registeredFieldOptions,
    stateOptions,
  ]);

  const [isHealthcareProvider, setIsHealthcareProvider] = useState<
    boolean | null
  >(initialIsHealthcareProvider);
  const [registeredField, setRegisteredField] = useState<string>('');
  const [registeredState, setRegisteredState] = useState<string>('');
  const [licenseNumber, setLicenseNumber] = useState<string>('');
  const [otherRegisteredField, setOtherRegisteredField] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState<number>(0);

  useEffect(() => {
    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const showSub = Keyboard.addListener(showEvent, e => {
      setKeyboardHeight(e.endCoordinates.height);
    });

    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  useEffect(() => {
    if (registeredFieldOptions.length === 0 || stateOptions.length === 0) {
      return;
    }

    const currentInitial = JSON.stringify(normalizedInitial);

    if (previousInitialRef.current === currentInitial) {
      return;
    }

    setIsHealthcareProvider(normalizedInitial.isHealthcareProvider);
    setRegisteredField(normalizedInitial.registeredField);
    setRegisteredState(normalizedInitial.registeredState);
    setLicenseNumber(normalizedInitial.licenseNumber);
    setOtherRegisteredField(normalizedInitial.otherRegisteredField);
    setHasSubmitted(false);
    setActiveDropdown(null);

    previousInitialRef.current = currentInitial;
  }, [normalizedInitial, registeredFieldOptions.length, stateOptions.length]);

  const fieldsEnabled = isHealthcareProvider === true;
  const showOtherField = fieldsEnabled && registeredField === 'other';

  const scrollToTop = (): void => {
    setTimeout(() => {
      scrollRef?.current?.scrollToPosition?.(0, 0, true);
    }, 100);
  };

  const errors = useMemo<HealthcareProviderErrors>(
    () => ({
      isHealthcareProvider:
        hasSubmitted && isHealthcareProvider === null
          ? String(tt('hcpSelectionError', 'Please select Yes or No'))
          : '',
      registeredField:
        hasSubmitted && fieldsEnabled && !registeredField
          ? String(tt('registeredFieldError', 'Please select registered field'))
          : '',
      otherRegisteredField:
        hasSubmitted && showOtherField && !otherRegisteredField.trim()
          ? String(
              tt(
                'otherRegisteredFieldError',
                'Please specify your healthcare provider field',
              ),
            )
          : '',
      registeredState:
        hasSubmitted && fieldsEnabled && !registeredState
          ? String(tt('registeredStateError', 'Please select registered state'))
          : '',
      licenseNumber:
        hasSubmitted && fieldsEnabled && !licenseNumber.trim()
          ? String(tt('licenseNumberError', 'Please enter license number'))
          : '',
    }),
    [
      hasSubmitted,
      isHealthcareProvider,
      fieldsEnabled,
      showOtherField,
      registeredField,
      otherRegisteredField,
      registeredState,
      licenseNumber,
    ],
  );

  const hasRequiredProviderFields =
    !!registeredField &&
    !!registeredState &&
    !!licenseNumber.trim() &&
    (!showOtherField || !!otherRegisteredField.trim());

  const canSave =
    isHealthcareProvider !== null &&
    (isHealthcareProvider === false || hasRequiredProviderFields);

  const isDirty = useMemo(
    () =>
      normalizedInitial.isHealthcareProvider !== isHealthcareProvider ||
      normalizedInitial.registeredField !== registeredField ||
      normalizedInitial.registeredState !== registeredState ||
      normalizedInitial.licenseNumber !== licenseNumber ||
      normalizedInitial.otherRegisteredField !== otherRegisteredField,
    [
      normalizedInitial,
      isHealthcareProvider,
      registeredField,
      registeredState,
      licenseNumber,
      otherRegisteredField,
    ],
  );

  const hasErrors = Object.values(errors).some(Boolean);

  const resetState = (): void => {
    setIsHealthcareProvider(normalizedInitial.isHealthcareProvider);
    setRegisteredField(normalizedInitial.registeredField);
    setRegisteredState(normalizedInitial.registeredState);
    setLicenseNumber(normalizedInitial.licenseNumber);
    setOtherRegisteredField(normalizedInitial.otherRegisteredField);
    setHasSubmitted(false);
    setActiveDropdown(null);
  };

  const handleHealthcareProviderChange = (value: boolean): void => {
    setIsHealthcareProvider(value);

    if (!value) {
      setActiveDropdown(null);
    }
  };

  const handleRegisteredFieldChange = (value: string): void => {
    setRegisteredField(value);

    if (value !== 'other') {
      setOtherRegisteredField('');
    }
  };

  const handleToggle = (id?: string): void => {
    if (!id) {
      setActiveDropdown(null);
      return;
    }

    setActiveDropdown(prev => (prev === id ? null : id));
  };

  const handleSave = async (): Promise<void> => {
    setHasSubmitted(true);

    if (isHealthcareProvider === null) {
      scrollToTop();
      return;
    }

    if (isHealthcareProvider === true && !hasRequiredProviderFields) {
      scrollToTop();
      return;
    }

    if (isHealthcareProvider === false) {
      await onSave?.({
        isHealthcareProvider: false,
        registeredField: '',
        registeredState: '',
        licenseNumber: '',
        otherRegisteredField: '',
      });
      return;
    }

    await onSave?.({
      isHealthcareProvider: true,
      registeredField:
        registeredField === 'other'
          ? otherRegisteredField.trim()
          : registeredField,
      registeredState:
        stateOptions.find(
          option => String(option.value) === String(registeredState),
        )?.label || registeredState,
      licenseNumber: licenseNumber.trim(),
      otherRegisteredField:
        registeredField === 'other' ? otherRegisteredField.trim() : '',
    });
  };

  return {
    values: {
      isHealthcareProvider,
      registeredField,
      registeredState,
      licenseNumber,
      otherRegisteredField,
      activeDropdown,
      errors,
      keyboardHeight,
    },
    derived: {
      isDirty,
      hasErrors,
      fieldsEnabled,
      showOtherField,
      hasSubmitted,
      canSave,
    },
    actions: {
      setRegisteredState,
      setLicenseNumber,
      setOtherRegisteredField,
      handleHealthcareProviderChange,
      handleRegisteredFieldChange,
      handleToggle,
      handleSave,
      resetState,
      closeDropdown: (): void => setActiveDropdown(null),
    },
    options: {
      registeredFields: registeredFieldOptions,
      states: stateOptions,
    },
  };
};
